import api from '../../../services/api';
import { useState, useEffect, useCallback } from "react";
import { useParams } from 'react-router-dom';
import { useForm } from "react-hook-form";
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PageContainer from '../../../common/components/PageContainer';
import useAlerta from '../../../common/components/useAlerta';
import AppSnackbar from '../../../common/components/AppSnackbar';
import ConfirmDialog from '../../../common/components/ConfirmDialog';
import { TIPOS_SERVICO, exigeRastreabilidadeResiduos, statusOSInfo, normalizarDatasOS } from '../osLabels';

function SectionTitle({ children }) {
    return (
        <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
                {children}
            </Typography>
            <Divider sx={{ mb: 2 }} />
        </>
    );
}

function Etapa({ osId, processo, editavel, onRemovido, mostrarAlerta }) {
    const [expandido, setExpandido] = useState(false);
    const [itens, setItens] = useState([]);
    const [carregado, setCarregado] = useState(false);
    const [novoItem, setNovoItem] = useState("");

    const carregarItens = async () => {
        try {
            const response = await api.get(`/os/${osId}/processos/${processo.id}/itens`);
            setItens(response.data);
            setCarregado(true);
        } catch (error) {
            console.error('Erro ao buscar itens da etapa:', error);
        }
    };

    const handleExpand = (_, isExpanded) => {
        setExpandido(isExpanded);
        if (isExpanded && !carregado) {
            carregarItens();
        }
    };

    const handleAddItem = async () => {
        if (!novoItem.trim()) return;
        try {
            await api.post(`/os/${osId}/processos/${processo.id}/itens`, { descricao: novoItem });
            setNovoItem("");
            carregarItens();
        } catch (error) {
            mostrarAlerta('Erro ao adicionar item da etapa!', 'error');
        }
    };

    const handleDeleteItem = async (idItem) => {
        try {
            await api.delete(`/os/${osId}/processos/${processo.id}/itens/${idItem}`);
            carregarItens();
        } catch (error) {
            mostrarAlerta('Erro ao remover item da etapa!', 'error');
        }
    };

    return (
        <Accordion expanded={expandido} onChange={handleExpand}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 1 }}>
                    <Chip size="small" label={`#${processo.sequencia}`} />
                    <Typography sx={{ flexGrow: 1 }}>{processo.descricao}</Typography>
                    <Typography variant="body2" color="text.secondary">{processo.local}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {processo.data ? new Date(processo.data).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : ''}
                    </Typography>
                    {editavel && (
                        <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => { e.stopPropagation(); onRemovido(processo.id); }}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Typography variant="caption" color="text.secondary">
                    Responsável: {processo.responsavel?.nome || 'Não informado'}
                </Typography>
                <Box sx={{ mt: 2 }}>
                    {itens.length === 0 && (
                        <Typography variant="body2" color="text.secondary">Nenhum item de checklist nesta etapa.</Typography>
                    )}
                    {itens.map((item) => (
                        <Stack key={item.id} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.5 }}>
                            <Typography variant="body2">{item.descricao}</Typography>
                            {editavel && (
                                <IconButton size="small" color="error" onClick={() => handleDeleteItem(item.id)}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Stack>
                    ))}
                </Box>
                {editavel && (
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <TextField
                            size="small"
                            label="Novo item de checklist"
                            value={novoItem}
                            onChange={(e) => setNovoItem(e.target.value)}
                            fullWidth
                        />
                        <Button variant="outlined" onClick={handleAddItem}>Adicionar</Button>
                    </Stack>
                )}
            </AccordionDetails>
        </Accordion>
    );
}

function OSItem() {
    const { id } = useParams();
    const { alerta, mostrarAlerta, fecharAlerta } = useAlerta();
    const { register, handleSubmit, reset } = useForm();
    const [os, setOs] = useState(null);
    const [funcionarios, setFuncionarios] = useState([]);
    const [funcionarioSelecionado, setFuncionarioSelecionado] = useState("");
    const [tipoServico, setTipoServico] = useState("");
    const [processos, setProcessos] = useState([]);
    const [novoProcesso, setNovoProcesso] = useState({ descricao: "", local: "", data: "", idResponsavel: "" });
    const [dialogFinal, setDialogFinal] = useState(false);
    const [dialogPosEntrega, setDialogPosEntrega] = useState(false);
    const [dialogCancelar, setDialogCancelar] = useState(false);
    const [conforme, setConforme] = useState(true);
    const [observacaoConformidade, setObservacaoConformidade] = useState("");
    const [clienteSatisfeito, setClienteSatisfeito] = useState(true);
    const [observacaoPosEntrega, setObservacaoPosEntrega] = useState("");

    const editavel = os?.status === 'RASCUNHO';
    const residuos = exigeRastreabilidadeResiduos(tipoServico);

    const carregarOS = useCallback(async () => {
        try {
            const responseOS = await api.get(`/os/${id}`);
            const osData = responseOS.data;
            setOs(osData);
            setFuncionarioSelecionado(osData.responsavel?.id || "");
            setTipoServico(osData.tipoServico || "");
            reset({
                dataEmissao: osData.dataEmissao || "",
                descricao: osData.descricao,
                empresa: osData.empresa,
                local: osData.local,
                cnpj: osData.cnpj,
                cpf: osData.cpf,
                outrosServicosDescricao: osData.outrosServicosDescricao,
                contratoNumero: osData.contratoNumero,
                contratoDataAssinatura: osData.contratoDataAssinatura,
                contratoItem: osData.contratoItem,
                condicoesMeioAmbiente: osData.condicoesMeioAmbiente,
                condicoesSeguranca: osData.condicoesSeguranca,
                metodosProcessosEquipamentos: osData.metodosProcessosEquipamentos,
                competenciasPessoal: osData.competenciasPessoal,
                numeroMtr: osData.numeroMtr,
                numeroSinir: osData.numeroSinir,
            });
        } catch (error) {
            console.error('Erro ao buscar a Ordem de serviço:', error);
        }
    }, [id, reset]);

    const carregarProcessos = useCallback(async () => {
        try {
            const response = await api.get(`/os/${id}/processos`);
            setProcessos(response.data);
        } catch (error) {
            console.error('Erro ao buscar etapas da OS:', error);
        }
    }, [id]);

    useEffect(() => {
        async function fetchDataFuncionarios() {
            try {
                const response = await api.get(`/funcionario/lista`);
                setFuncionarios(response.data);
            } catch (error) {
                console.error('Erro ao buscar funcinarios:', error);
            }
        }
        fetchDataFuncionarios();
        carregarOS();
        carregarProcessos();
    }, [carregarOS, carregarProcessos]);

    const onSubmit = async (data) => {
        const formData = normalizarDatasOS({
            ...data,
            tipoServico,
            responsavelId: funcionarioSelecionado,
        });

        try {
            await api.put(`/os/${id}`, formData);
            mostrarAlerta('Ordem de serviço atualizada com sucesso!', 'success');
            carregarOS();
        } catch (error) {
            mostrarAlerta(error?.response?.data || 'Erro ao atualizar a ordem de serviço!', 'error');
        }
        setTimeout(fecharAlerta, 3000);
    };

    const handleLiberarInicial = async () => {
        try {
            const response = await api.post(`/os/${id}/liberar-inicial`);
            setOs(response.data);
            mostrarAlerta('OS liberada para início dos trabalhos!', 'success');
        } catch (error) {
            mostrarAlerta(error?.response?.data || 'Erro ao liberar a OS!', 'error');
        }
        setTimeout(fecharAlerta, 3000);
    };

    const handleLiberarFinal = async () => {
        try {
            const response = await api.post(`/os/${id}/liberar-final`, { conforme, observacaoConformidade });
            setOs(response.data);
            setDialogFinal(false);
            mostrarAlerta('OS liberada para conclusão!', 'success');
        } catch (error) {
            mostrarAlerta(error?.response?.data || 'Erro ao liberar a conclusão da OS!', 'error');
        }
        setTimeout(fecharAlerta, 3000);
    };

    const handlePosEntrega = async () => {
        try {
            const response = await api.post(`/os/${id}/pos-entrega`, { clienteSatisfeito, observacaoPosEntrega });
            setOs(response.data);
            setDialogPosEntrega(false);
            mostrarAlerta('Satisfação do cliente registrada!', 'success');
        } catch (error) {
            mostrarAlerta(error?.response?.data || 'Erro ao registrar satisfação do cliente!', 'error');
        }
        setTimeout(fecharAlerta, 3000);
    };

    const handleCancelar = async () => {
        try {
            const response = await api.post(`/os/${id}/cancelar`);
            setOs(response.data);
            setDialogCancelar(false);
            mostrarAlerta('OS cancelada!', 'success');
        } catch (error) {
            mostrarAlerta(error?.response?.data || 'Erro ao cancelar a OS!', 'error');
        }
        setTimeout(fecharAlerta, 3000);
    };

    const handleAddProcesso = async () => {
        if (!novoProcesso.descricao || !novoProcesso.idResponsavel) {
            mostrarAlerta('Descrição e responsável da etapa são obrigatórios!', 'error');
            return;
        }
        try {
            await api.post(`/os/${id}/processos`, { ...novoProcesso, data: novoProcesso.data || null });
            setNovoProcesso({ descricao: "", local: "", data: "", idResponsavel: "" });
            carregarProcessos();
        } catch (error) {
            mostrarAlerta(error?.response?.data || 'Erro ao adicionar etapa!', 'error');
        }
    };

    const handleRemoverProcesso = async (idProcesso) => {
        try {
            await api.delete(`/os/${id}/processos/${idProcesso}`);
            carregarProcessos();
        } catch (error) {
            mostrarAlerta(error?.response?.data || 'Erro ao remover etapa!', 'error');
        }
    };

    const handleAbrirPdf = async () => {
        try {
            const response = await api.get(`/os/${id}/pdf`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error) {
            mostrarAlerta('Erro ao gerar o PDF da OS!', 'error');
        }
    };

    if (!os) {
        return (
            <PageContainer sx={{ maxWidth: 900 }}>
                <Typography>Carregando...</Typography>
            </PageContainer>
        );
    }

    const status = statusOSInfo(os.status);

    return (
        <PageContainer sx={{ maxWidth: 900 }}>
            <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
                    <Box>
                        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600 }}>
                            {os.numeroOs || 'Ordem de Serviço'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Criada por {os.criadoPor?.nome || 'não identificado'}
                            {os.dataCriacao ? ` em ${new Date(os.dataCriacao).toLocaleDateString('pt-BR')}` : ''}
                        </Typography>
                    </Box>
                    <Chip label={status.label} color={status.color} />
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                    {os.status === 'RASCUNHO' && (
                        <Button variant="contained" color="secondary" onClick={handleLiberarInicial}>
                            Liberar Início
                        </Button>
                    )}
                    {os.status === 'LIBERADA_INICIO' && (
                        <Button variant="contained" color="secondary" onClick={() => setDialogFinal(true)}>
                            Liberar Conclusão
                        </Button>
                    )}
                    {os.status === 'LIBERADA_FINAL' && os.clienteSatisfeito == null && (
                        <Button variant="outlined" onClick={() => setDialogPosEntrega(true)}>
                            Registrar Satisfação do Cliente
                        </Button>
                    )}
                    {(os.status === 'RASCUNHO' || os.status === 'LIBERADA_INICIO') && (
                        <Button variant="outlined" color="error" onClick={() => setDialogCancelar(true)}>
                            Cancelar OS
                        </Button>
                    )}
                    <Button variant="outlined" startIcon={<PictureAsPdfOutlinedIcon />} onClick={handleAbrirPdf}>
                        Baixar PDF
                    </Button>
                </Stack>

                {os.status === 'LIBERADA_INICIO' && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Liberada para início por {os.liberadoPorInicial?.nome} em {os.dataLiberacaoInicial}.
                    </Alert>
                )}
                {(os.status === 'LIBERADA_FINAL' || os.status === 'CANCELADA') && (
                    <Alert severity={os.status === 'CANCELADA' ? 'error' : 'success'} sx={{ mt: 2 }}>
                        {os.status === 'CANCELADA'
                            ? 'Esta OS foi cancelada.'
                            : `Liberada para conclusão por ${os.liberadoPorFinal?.nome} em ${os.dataLiberacaoFinal}. Conforme: ${os.conforme ? 'Sim' : 'Não'}.`}
                    </Alert>
                )}
            </Paper>

            <Paper component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: { xs: 2, sm: 4 } }}>
                <Typography variant="h6">Dados da Ordem de Serviço</Typography>
                {!editavel && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Esta OS não está mais em rascunho, então os dados abaixo não podem mais ser editados.
                    </Alert>
                )}

                <SectionTitle>Identificação</SectionTitle>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField {...register("descricao")} label="Descrição do serviço" required fullWidth multiline minRows={2} disabled={!editavel} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField {...register("empresa")} label="Empresa/Cliente" required fullWidth disabled={!editavel} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField {...register("local")} label="Local da execução" required fullWidth disabled={!editavel} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField {...register("cnpj")} label="CNPJ" fullWidth disabled={!editavel} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField {...register("cpf")} label="CPF" fullWidth disabled={!editavel} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField {...register("dataEmissao")} label="Data de emissão" type="date" InputLabelProps={{ shrink: true }} fullWidth disabled={!editavel} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            label="Responsável técnico"
                            value={funcionarioSelecionado}
                            onChange={(e) => setFuncionarioSelecionado(e.target.value)}
                            required
                            fullWidth
                            disabled={!editavel}
                        >
                            <MenuItem value="">Selecione o responsável</MenuItem>
                            {funcionarios.map(funcionario => (
                                <MenuItem key={funcionario.id} value={funcionario.id}>
                                    {funcionario.nome}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            label="Tipo de serviço"
                            value={tipoServico}
                            onChange={(e) => setTipoServico(e.target.value)}
                            required
                            fullWidth
                            disabled={!editavel}
                        >
                            <MenuItem value="">Selecione o tipo de serviço</MenuItem>
                            {TIPOS_SERVICO.map((tipo) => (
                                <MenuItem key={tipo.value} value={tipo.value}>{tipo.label}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    {tipoServico === 'OUTROS' && (
                        <Grid item xs={12}>
                            <TextField {...register("outrosServicosDescricao")} label="Descreva o tipo de serviço" fullWidth disabled={!editavel} InputLabelProps={{ shrink: true }} />
                        </Grid>
                    )}
                </Grid>

                <SectionTitle>Contrato</SectionTitle>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField {...register("contratoNumero")} label="Número do contrato" fullWidth disabled={!editavel} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField {...register("contratoDataAssinatura")} label="Data de assinatura" type="date" InputLabelProps={{ shrink: true }} fullWidth disabled={!editavel} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField {...register("contratoItem")} label="Item do contrato" fullWidth disabled={!editavel} InputLabelProps={{ shrink: true }} />
                    </Grid>
                </Grid>

                <SectionTitle>Condições e normas (NR-1 / NR-18 / NR-35 / NR-33 / NR-12)</SectionTitle>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField {...register("condicoesMeioAmbiente")} label="Condições de meio ambiente" fullWidth multiline minRows={2} disabled={!editavel} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField {...register("condicoesSeguranca")} label="Condições de segurança" fullWidth multiline minRows={2} disabled={!editavel} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField {...register("metodosProcessosEquipamentos")} label="Métodos, processos e equipamentos" fullWidth multiline minRows={2} disabled={!editavel} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField {...register("competenciasPessoal")} label="Competências de pessoal exigidas" fullWidth multiline minRows={2} disabled={!editavel} InputLabelProps={{ shrink: true }} />
                    </Grid>
                </Grid>

                <SectionTitle>Rastreabilidade de resíduos (PNRS / MTR nacional - SINIR)</SectionTitle>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField {...register("numeroMtr")} label="Número do MTR" required={residuos} fullWidth disabled={!editavel} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField {...register("numeroSinir")} label="Número do SINIR" fullWidth disabled={!editavel} InputLabelProps={{ shrink: true }} />
                    </Grid>
                </Grid>

                {editavel && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <Button type="submit" variant="contained" color="secondary" size="large" sx={{ px: 6 }}>
                            Atualizar
                        </Button>
                    </Box>
                )}
            </Paper>

            <Paper sx={{ p: { xs: 2, sm: 4 }, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Etapas de Execução</Typography>

                {processos.length === 0 && (
                    <Typography color="text.secondary" sx={{ mb: 2 }}>Nenhuma etapa registrada ainda.</Typography>
                )}
                <Stack spacing={1} sx={{ mb: 3 }}>
                    {processos.map((processo) => (
                        <Etapa
                            key={processo.id}
                            osId={id}
                            processo={processo}
                            editavel={os.status !== 'LIBERADA_FINAL'}
                            onRemovido={handleRemoverProcesso}
                            mostrarAlerta={mostrarAlerta}
                        />
                    ))}
                </Stack>

                {os.status !== 'LIBERADA_FINAL' && os.status !== 'CANCELADA' && (
                    <>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Adicionar etapa</Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Descrição"
                                    value={novoProcesso.descricao}
                                    onChange={(e) => setNovoProcesso({ ...novoProcesso, descricao: e.target.value })}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="Local"
                                    value={novoProcesso.local}
                                    onChange={(e) => setNovoProcesso({ ...novoProcesso, local: e.target.value })}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <TextField
                                    label="Data"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={novoProcesso.data}
                                    onChange={(e) => setNovoProcesso({ ...novoProcesso, data: e.target.value })}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <TextField
                                    select
                                    label="Responsável"
                                    value={novoProcesso.idResponsavel}
                                    onChange={(e) => setNovoProcesso({ ...novoProcesso, idResponsavel: e.target.value })}
                                    fullWidth
                                    size="small"
                                >
                                    <MenuItem value="">Selecione</MenuItem>
                                    {funcionarios.map((f) => (
                                        <MenuItem key={f.id} value={f.id}>{f.nome}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <Button variant="contained" color="secondary" onClick={handleAddProcesso}>
                                    Adicionar etapa
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                )}
            </Paper>

            {/* Liberação final / conformidade */}
            <Dialog open={dialogFinal} onClose={() => setDialogFinal(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Liberar conclusão da OS</DialogTitle>
                <DialogContent>
                    <FormControlLabel
                        control={<Checkbox checked={conforme} onChange={(e) => setConforme(e.target.checked)} />}
                        label="O serviço está conforme"
                    />
                    <TextField
                        label="Observação de conformidade"
                        value={observacaoConformidade}
                        onChange={(e) => setObservacaoConformidade(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogFinal(false)} color="inherit">Cancelar</Button>
                    <Button onClick={handleLiberarFinal} variant="contained" color="secondary">Confirmar</Button>
                </DialogActions>
            </Dialog>

            {/* Pós-entrega */}
            <Dialog open={dialogPosEntrega} onClose={() => setDialogPosEntrega(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Satisfação do cliente</DialogTitle>
                <DialogContent>
                    <FormControlLabel
                        control={<Checkbox checked={clienteSatisfeito} onChange={(e) => setClienteSatisfeito(e.target.checked)} />}
                        label="Cliente satisfeito"
                    />
                    <TextField
                        label="Observação"
                        value={observacaoPosEntrega}
                        onChange={(e) => setObservacaoPosEntrega(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogPosEntrega(false)} color="inherit">Cancelar</Button>
                    <Button onClick={handlePosEntrega} variant="contained" color="secondary">Salvar</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={dialogCancelar}
                title="Cancelar OS"
                message="Tem certeza que deseja cancelar esta Ordem de Serviço? Essa ação não pode ser desfeita."
                confirmLabel="Cancelar OS"
                confirmColor="error"
                onConfirm={handleCancelar}
                onClose={() => setDialogCancelar(false)}
            />

            <AppSnackbar alerta={alerta} onClose={fecharAlerta} />
        </PageContainer>
    );
}

export default OSItem;
